interface Route {
	facetFilters?: {
		'facets.hierarchicalInterests'?: string[];
		fundingLevel?: string[];
		genericRoles?: string[];
		programType?: string[];
		specificRoles?: string[];
		'studentFacingAcademicPartner.name'?: string[];
	};
	fundingLevel?: string[] | Record<string, string[]>;
	interests?: string[] | Record<string, string[]>;
	learningProvider?: string[] | Record<string, string[]>;
	programType?: string[] | Record<string, string[]>;
}

const oldFacetNamesToNewFacetNames = {
	'Certificate < Advanced Certificate': 'Certificates & bootcamps > Advanced',
	'Certificate < Intermediate Certificate': 'Certificates & bootcamps > Intermediate',
	'Certificate < Introductory Certificate': 'Certificates & bootcamps > Introductory',
	'High School': 'High school diplomas',
	Language: 'Language programs',
	"Master's": "Master's degrees",
};

export const getFacetValue = (value: string): string => {
	if (value.includes('Certificate <')) {
		return 'Certificates & bootcamps';
	}
	if (value.includes('Business <')) {
		return 'Business';
	}
	if (value.includes('Healthcare <')) {
		return 'Healthcare';
	}
	if (value.includes('Technology < ')) {
		return 'Technology';
	}
	if (Object.keys(oldFacetNamesToNewFacetNames).includes(value)) {
		return oldFacetNamesToNewFacetNames[value];
	}

	return value;
};

export const getParentValueForFacet = (
	route: Route,
	oldFacetType: string,
	newFacetType: string
): string[] => {
	if (route?.facetFilters && Object.keys(route?.facetFilters).includes(oldFacetType)) {
		return route?.facetFilters[oldFacetType]
			.filter(facet => !facet.includes('>'))
			.map(value => {
				return getFacetValue(value);
			});
	} else if (route?.[newFacetType]) {
		return route?.[newFacetType].filter(facet => !facet.includes('>'));
	} else {
		return null;
	}
};

export const getChildValueForFacet = (
	route: Route,
	oldFacetType: string,
	newFacetType: string
): string[] => {
	if (route?.facetFilters && route?.facetFilters[oldFacetType]) {
		return route?.facetFilters[oldFacetType]
			.filter(facet => facet.includes('<'))
			.map(value => {
				if (Object.keys(oldFacetNamesToNewFacetNames).includes(value)) {
					return oldFacetNamesToNewFacetNames[value];
				} else {
					return value.replace('<', '>');
				}
			});
	} else if (route?.[newFacetType]) {
		return route?.[newFacetType].filter(facet => facet.includes('>'));
	} else {
		return null;
	}
};

const checkForDefaults = attribute => {
	if (attribute === 'Any time to complete' || attribute === 'Any hours per week') {
		return null;
	} else {
		return attribute;
	}
};

export const formatRouteToState = (state, index: string) => {
	const newUiState = {
		[index]: {
			menu: {
				completionTimeRange: state?.completionTimeRange
					? checkForDefaults(state?.completionTimeRange)
					: null,
				weeklyEffortRange: state?.weeklyEffortRange
					? checkForDefaults(state?.weeklyEffortRange)
					: null,
			},
			refinementList: {
				fundingLevel:
					state?.fundingLevel || state?.facetFilters
						? getParentValueForFacet(state, 'fundingLevel', 'fundingLevel')
						: null,
				'genericRoles.lvl0':
					state?.roles || state?.facetFilters
						? getParentValueForFacet(state, 'genericRoles', 'genericRoles')
						: null,
				'genericRoles.lvl1':
					state?.roles || state?.facetFilters
						? getChildValueForFacet(state, 'genericRoles', 'genericRoles')
						: null,
				'hierarchicalInterests.lvl0':
					state?.interests || state?.facetFilters
						? getParentValueForFacet(state, 'facets.hierarchicalInterests', 'interests')
						: null,
				'hierarchicalInterests.lvl1':
					state?.interests || state?.facetFilters
						? getChildValueForFacet(state, 'facets.hierarchicalInterests', 'interests')
						: null,
				'programType.lvl0':
					state?.programType || state?.facetFilters
						? getParentValueForFacet(state, 'programType', 'programType')
						: null,
				'programType.lvl1':
					state?.programType || state?.facetFilters
						? getChildValueForFacet(state, 'programType', 'programType')
						: null,
				'specificRoles.lvl0':
					state?.roles || state?.facetFilters
						? getParentValueForFacet(state, 'specificRoles', 'specificRoles')
						: null,
				'specificRoles.lvl1':
					state?.roles || state?.facetFilters
						? getChildValueForFacet(state, 'specificRoles', 'specificRoles')
						: null,
				'studentFacingAcademicPartner.name':
					state?.learningProvider || state?.facetFilters
						? getParentValueForFacet(state, 'studentFacingAcademicPartner.name', 'learningProvider')
						: null,
			},
			page: state?.page ? parseInt(state.page) + 1 : 0,
			query: state?.query ? state.query : '',
		},
	};
	return newUiState;
};

type RouteConfig = {
	completionTimeRange?: string;
	fundingLevel?: string;
	genericRoles?: string[];
	interests?: string[];
	learningProvider?: string;
	page?: number;
	programType?: string[];
	query?: string;
	specificRoles?: string[];
	weeklyEffortRange?: string;
};

export const formatStateToRoute = (uiState, index: string) => {
	const state = uiState[index];
	const routeConfig: RouteConfig = {
		query: state.query || '',
		page: state.page > 0 ? state.page - 1 : 0 || 0,
	};

	if (
		state.menu &&
		state.menu.completionTimeRange &&
		checkForDefaults(state.menu.completionTimeRange) !== null
	) {
		routeConfig.completionTimeRange = state.menu.completionTimeRange;
	}

	if (
		state.menu &&
		state.menu.weeklyEffortRange &&
		checkForDefaults(state.menu.weeklyEffortRange) !== null
	) {
		routeConfig.weeklyEffortRange = state.menu.weeklyEffortRange;
	}

	if (state.refinementList && state.refinementList.fundingLevel) {
		routeConfig.fundingLevel = state.refinementList.fundingLevel;
	}

	if (state.refinementList && state.refinementList['hierarchicalInterests.lvl0']) {
		if (
			state.refinementList['hierarchicalInterests.lvl0'] &&
			state.refinementList['hierarchicalInterests.lvl1']
		) {
			routeConfig.interests = [
				...state.refinementList['hierarchicalInterests.lvl0'],
				...state.refinementList['hierarchicalInterests.lvl1'],
			];
		}
		if (
			state.refinementList['hierarchicalInterests.lvl0'] &&
			!state.refinementList['hierarchicalInterests.lvl1']
		) {
			routeConfig.interests = [...state.refinementList['hierarchicalInterests.lvl0']];
		}
	}

	if (state.refinementList && state.refinementList['studentFacingAcademicPartner.name']) {
		routeConfig.learningProvider = state.refinementList['studentFacingAcademicPartner.name'];
	}

	if (state.refinementList && state.refinementList['programType.lvl0']) {
		if (state.refinementList['programType.lvl0'] && state.refinementList?.['programType.lvl1']) {
			routeConfig.programType = [
				...state.refinementList['programType.lvl0'],
				...state.refinementList['programType.lvl1'],
			];
		}
		if (state.refinementList['programType.lvl0'] && !state.refinementList?.['programType.lvl1']) {
			routeConfig.programType = [...state.refinementList['programType.lvl0']];
		}
	}

	if (state.refinementList && state.refinementList['genericRoles.lvl0']) {
		if (state.refinementList['genericRoles.lvl0'] && state.refinementList?.['genericRoles.lvl1']) {
			routeConfig.genericRoles = [
				...state.refinementList['genericRoles.lvl0'],
				...state.refinementList['genericRoles.lvl1'],
			];
		}
		if (state.refinementList['genericRoles.lvl0'] && !state.refinementList?.['genericRoles.lvl1']) {
			routeConfig.genericRoles = [...state.refinementList['genericRoles.lvl0']];
		}
	}

	if (state.refinementList && state.refinementList['specificRoles.lvl0']) {
		if (
			state.refinementList['specificRoles.lvl0'] &&
			state.refinementList?.['specificRoles.lvl1']
		) {
			routeConfig.specificRoles = [
				...state.refinementList['specificRoles.lvl0'],
				...state.refinementList['specificRoles.lvl1'],
			];
		}
		if (
			state.refinementList['specificRoles.lvl0'] &&
			!state.refinementList?.['specificRoles.lvl1']
		) {
			routeConfig.specificRoles = [...state.refinementList['specificRoles.lvl0']];
		}
	}

	return routeConfig;
};
